import React, { useEffect, useState } from 'react';
import { AlertTriangle, Loader2, Activity, RefreshCw } from 'lucide-react';
import { useEmbedToken } from '../lib/embedTokenBridge';
import { emit } from '../lib/embedChannel';

/**
 * Embed activity feed — mounts at `/embed/v2/activity`. Consumes any
 * valid embed JWT (viewer or editor) and lists the workspace-wide
 * dashboard save history for the JWT's workspace. Meant to be dropped
 * into a host's admin surface so they can see who edited what across
 * every dashboard without leaving their app.
 *
 * Strict isolation: server-side the listing is bounded by the JWT's
 * (tenant_id, workspace_id), so a customer JWT can never see another
 * customer's activity even inside a shared tenant.
 */

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function EmbedActivityFeedPage() {
  const token = useEmbedToken();
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');
  // Server-side filters — sent as query params on each fetch.
  const [dashboardFilter, setDashboardFilter] = useState('');
  const [since, setSince] = useState('');
  const [until, setUntil] = useState('');

  const load = React.useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const url = new URL(`${API}/api/embed/v2/workspace/activity`);
      url.searchParams.set('limit', '100');
      if (dashboardFilter) url.searchParams.set('dashboard_id', dashboardFilter);
      // `<input type="date">` gives YYYY-MM-DD; server compares as ISO
      // strings, so we widen to a full-day boundary.
      if (since) url.searchParams.set('since', `${since}T00:00:00+00:00`);
      if (until) url.searchParams.set('until', `${until}T23:59:59.999+00:00`);
      const res = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) {
        const b = await res.json().catch(() => ({}));
        throw new Error(b?.detail?.message || `HTTP ${res.status}`);
      }
      const body = await res.json();
      setRows(Array.isArray(body?.data) ? body.data : []);
      emit('activity.loaded', {
        count: body?.count ?? 0,
        filters: body?.filters,
      });
    } catch (e) {
      setError(String(e?.message || e));
      setRows([]);
      emit('error', { source: 'activity', message: String(e?.message || e) });
    } finally {
      setLoading(false);
    }
  }, [token, dashboardFilter, since, until]);

  useEffect(() => { load(); }, [load]);

  // Distinct dashboards inside the current result set — feed the
  // dashboard filter dropdown from whatever rows we already have so
  // hosts don't need to pre-know every dashboard_id.
  const dashboardOptions = React.useMemo(() => {
    if (!Array.isArray(rows)) return [];
    const seen = new Map();
    rows.forEach(r => {
      if (r.dashboard_id && !seen.has(r.dashboard_id)) {
        seen.set(r.dashboard_id, r.dashboard_name || r.dashboard_id);
      }
    });
    return Array.from(seen.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [rows]);

  const clearFilters = () => {
    setDashboardFilter('');
    setSince('');
    setUntil('');
    setQuery('');
  };
  const hasFilters = Boolean(dashboardFilter || since || until || query);

  const filtered = React.useMemo(() => {
    if (!Array.isArray(rows)) return rows;
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(r => {
      const hay = [r.dashboard_name, r.snapshot_name, r.note, r.saved_by].filter(Boolean).join(' ').toLowerCase();
      return hay.includes(q);
    });
  }, [rows, query]);

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-background text-xs text-muted-foreground" data-testid="embed-activity-no-token">
        Missing embed token
      </div>
    );
  }
  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="embed-activity-page">
      <div className="max-w-3xl mx-auto p-5">
        <div className="flex items-start gap-3 mb-4 pb-3 border-b border-border/25">
          <Activity className="w-6 h-6 mt-0.5 text-emerald-400" />
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Workspace Activity</div>
            <div className="text-lg font-semibold">Recent dashboard saves</div>
            <div className="text-xs text-muted-foreground mt-1">
              Every save across every dashboard in this workspace, newest first.
            </div>
          </div>
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="text-muted-foreground/70 hover:text-indigo-300 disabled:opacity-40 shrink-0 mt-1"
            title="Refresh"
            aria-label="Refresh activity feed"
            data-testid="embed-activity-refresh"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {error && (
          <div className="mb-4 rounded-md border border-rose-500/40 bg-rose-500/5 p-3 text-xs text-rose-300 flex items-center gap-2" data-testid="embed-activity-error">
            <AlertTriangle className="w-4 h-4" /> {error}
          </div>
        )}

        {Array.isArray(rows) && (rows.length > 0 || dashboardFilter || since || until) && (
          <div className="mb-3 grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid="embed-activity-filters">
            <label className="text-[10px] uppercase tracking-wider text-muted-foreground flex flex-col gap-1">
              Dashboard
              <select
                value={dashboardFilter}
                onChange={e => setDashboardFilter(e.target.value)}
                className="bg-transparent border border-border/40 rounded-md px-2 py-1 text-xs outline-none focus:border-indigo-400/60 normal-case tracking-normal"
                data-testid="embed-activity-filter-dashboard"
              >
                <option value="">All dashboards</option>
                {dashboardOptions.map(([id, name]) => (
                  <option key={id} value={id}>{name}</option>
                ))}
              </select>
            </label>
            <label className="text-[10px] uppercase tracking-wider text-muted-foreground flex flex-col gap-1">
              From
              <input
                type="date"
                value={since}
                onChange={e => setSince(e.target.value)}
                className="bg-transparent border border-border/40 rounded-md px-2 py-1 text-xs outline-none focus:border-indigo-400/60"
                data-testid="embed-activity-filter-since"
              />
            </label>
            <label className="text-[10px] uppercase tracking-wider text-muted-foreground flex flex-col gap-1">
              To
              <input
                type="date"
                value={until}
                onChange={e => setUntil(e.target.value)}
                className="bg-transparent border border-border/40 rounded-md px-2 py-1 text-xs outline-none focus:border-indigo-400/60"
                data-testid="embed-activity-filter-until"
              />
            </label>
          </div>
        )}

        {hasFilters && (
          <div className="mb-2 flex justify-end">
            <button
              type="button"
              onClick={clearFilters}
              className="text-[10px] text-muted-foreground hover:text-indigo-300 underline underline-offset-2"
              data-testid="embed-activity-clear-filters"
            >
              Clear filters
            </button>
          </div>
        )}

        {Array.isArray(rows) && rows.length > 0 && (
          <input
            type="search"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Filter by dashboard, note, or author…"
            className="w-full mb-3 bg-transparent border border-border/40 rounded-md px-2 py-1.5 text-xs outline-none focus:border-indigo-400/60"
            data-testid="embed-activity-search"
          />
        )}

        {loading && !rows && (
          <div className="text-xs text-muted-foreground flex items-center gap-2" data-testid="embed-activity-loading">
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading activity…
          </div>
        )}

        {Array.isArray(filtered) && filtered.length === 0 && !loading && (
          <div className="text-xs text-muted-foreground border border-dashed border-border/40 rounded-md p-6 text-center" data-testid="embed-activity-empty">
            {query
              ? `No entries matching “${query}”`
              : rows?.length === 0
                ? 'No saves recorded yet in this workspace.'
                : 'No entries matching your filter.'}
          </div>
        )}

        {Array.isArray(filtered) && filtered.length > 0 && (
          <ul className="space-y-1.5" data-testid="embed-activity-list">
            {filtered.map(r => (
              <li
                key={r.version_id}
                className="rounded-md border border-border/30 bg-background/40 px-3 py-2"
                data-testid={`embed-activity-row-${r.version_id}`}
              >
                <div className="flex items-baseline justify-between gap-3">
                  <div className="text-sm font-medium truncate">{r.dashboard_name}</div>
                  <div className="text-[10px] text-muted-foreground font-mono shrink-0">
                    {r.saved_at ? new Date(r.saved_at).toLocaleString() : '—'}
                  </div>
                </div>
                <div className="text-[10px] text-muted-foreground flex items-center gap-2 flex-wrap mt-0.5">
                  <span className="font-mono">by {r.saved_by || 'unknown'}</span>
                  <span>·</span>
                  <span>{r.widget_count ?? 0} widgets at save</span>
                  {r.snapshot_name && r.snapshot_name !== r.dashboard_name && (
                    <>
                      <span>·</span>
                      <span>was named “{r.snapshot_name}”</span>
                    </>
                  )}
                </div>
                {r.note && (
                  <div
                    className="mt-1 text-[11px] italic text-muted-foreground/90 line-clamp-3"
                    data-testid={`embed-activity-note-${r.version_id}`}
                  >
                    “{r.note}”
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}

        {Array.isArray(rows) && rows.length > 0 && (
          <div
            className="mt-3 text-[10px] text-muted-foreground text-right"
            data-testid="embed-activity-counts"
          >
            {query
              ? `${filtered.length} of ${rows.length} matching`
              : `${rows.length} saves shown`}
          </div>
        )}
      </div>
    </div>
  );
}
